import type { FeatureDeepDiveContent } from '@/pages/public/feature-deep-dive';

export const featureDeepDiveContent: Record<string, FeatureDeepDiveContent> = {
  '/crm-with-role-based-access': {
    path: '/crm-with-role-based-access',
    eyebrow: 'CRM with role-based access',
    heroTitle: 'A sales CRM with role-based access control for admins, managers, and reps',
    heroBody:
      'Protect sensitive pipeline data and enforce operational workflows with role-based permissions designed for clear admin oversight, manager coaching, and rep execution.',
    heroCta: 'Explore role-based access',
    featureOverview: {
      title: 'Role-Based Access Control',
      description:
        'Explicit permissions for Admin, Manager, and Salesman roles keep data clean and ownership clear.',
      icon: 'shield',
    },
    technicalDetails: {
      title: 'How LeaderDaddy enforces role boundaries',
      description:
        'LeaderDaddy enforces strict server authorization across Admin, Manager, and Salesman roles, ensuring reps focus on execution while admins maintain system governance.',
      bullets: [
        'Admins configure workspace settings, products, team permissions, and billing controls',
        'Managers inspect team pipeline reports, reassign leads, and track overdue follow-ups',
        'Salespeople work with leads they created or are assigned; managers work within their own and direct-report scope',
        'Server-side authorization prevents unauthorized data access or modification',
        'Audit history tracks important workspace activity and settings changes',
      ],
    },
    useCases: {
      title: 'When role-based access matters most',
      description: 'Multi-tier sales structures need clear data boundaries to prevent chaos.',
      items: [
        {
          title: 'Sales organizations with team leaders',
          description:
            'Managers need visibility into team performance without exposing sensitive pricing or admin settings.',
        },
        {
          title: 'Agencies with client-facing account managers',
          description:
            'Company workspaces and lead ownership define access. These team roles do not provide a separate external client or partner portal.',
        },
        {
          title: 'Companies needing strict data boundaries',
          description:
            'Regulated industries or competitive markets need to control who sees what inside the CRM.',
        },
        {
          title: 'Growing sales teams adding sales reps',
          description:
            'New reps work with leads they created or are assigned. The Lead Analytics workspace is limited to admins and managers.',
        },
      ],
    },
    integrationTitle: 'Role-based integration points',
    integrationDescription:
      'LeaderDaddy roles integrate with every module to enforce boundaries automatically.',
    integrationPoints: [
      'Lead assignment and visibility controlled by role',
      'Report access filtered by management hierarchy',
      'Settings and billing restricted to Admin role',
      'Team management visible to Managers and Admins only',
      'Audit logs capture role-based actions for compliance',
      'API endpoints enforce role checks on every request',
    ],
    benefitsTitle: 'Why role clarity improves productivity',
    benefits: [
      {
        title: 'Admin oversight',
        description:
          'Configure workspace settings, products, team permissions, and billing controls.',
      },
      {
        title: 'Manager coaching',
        description: 'Inspect team pipeline reports, reassign leads, and track overdue follow-ups.',
      },
      {
        title: 'Salesperson focus',
        description:
          'Reps get a workspace for leads they created or are assigned, with next actions and activity history.',
      },
    ],
    industriesTitle: 'Built for multi-tier sales structures',
    industries: [
      'Sales organizations with team leaders',
      'Agencies with client-facing account managers',
      'Companies needing strict data boundaries',
      'Growing sales teams adding sales reps',
      'Multi-department lead handling desks',
    ],
    faqs: [
      {
        question: 'What is role-based access in a CRM?',
        answer:
          "Role-based access control (RBAC) restricts feature visibility and data editing based on a user's job function within the sales organization.",
      },
      {
        question: 'How does LeaderDaddy enforce role boundaries?',
        answer:
          'Server authorization limits admins to their company workspace, managers to leads they created or are assigned and leads belonging to their direct reports, and salespeople to leads they created or are assigned. Lead Analytics is available only to admins and managers in their permitted scope.',
      },
      {
        question: 'Can I create custom roles?',
        answer:
          'Currently, LeaderDaddy supports three primary roles: Admin, Manager, and Salesman. Each role has explicit permissions tailored to common sales organizational structures.',
      },
      {
        question: 'Does LeaderDaddy include an external partner portal?',
        answer:
          'No separate external partner portal is included in these CRM roles. Admin, Manager and Salesperson permissions organize internal team access; do not use them as a promise of custom partner visibility or client-specific permissions.',
      },
    ],
  },
  '/crm-with-whatsapp-integration': {
    path: '/crm-with-whatsapp-integration',
    eyebrow: 'CRM with WhatsApp integration',
    heroTitle: 'WhatsApp CRM for lead conversations and follow-ups',
    heroBody:
      'Connect WhatsApp Business to your CRM. Manage lead conversations, assign follow-ups and send template campaigns. Check account eligibility and messaging costs.',
    heroCta: 'Start your 7-day CRM trial',
    featureOverview: {
      title: 'WhatsApp connected to your sales CRM',
      description:
        'Move from a customer message to a lead, an owner and a follow-up without losing conversation context.',
      icon: 'messageSquare',
    },
    technicalDetails: {
      title: 'How WhatsApp integration works in LeaderDaddy',
      description:
        'An administrator connects the business account through Meta onboarding. Once the number is active, the team can use the inbox, sync account templates, select campaign audiences and follow up in the CRM.',
      bullets: [
        'Connect your WhatsApp Business account and eligible phone number through Meta signup',
        'Check the connection and sync approved account templates',
        'Open customer conversations in the inbox with the associated lead context',
        'Assign lead ownership and record the next follow-up in the CRM',
        'Preview a relevant template for a consenting audience, then send or schedule it',
        'Review campaign recipient states and replies before deciding the next action',
      ],
    },
    useCases: {
      title: 'Who should use WhatsApp CRM integration',
      description:
        'Teams that rely on faster conversational follow-up and messaging-native customer engagement.',
      items: [
        {
          title: 'Businesses that sell through WhatsApp conversations',
          description: 'Keep lead context in the CRM while carrying conversations on WhatsApp.',
        },
        {
          title: 'Teams sharing product information',
          description:
            'Configured product and catalog workflows can support product replies. Confirm your catalog connection and eligible products during setup.',
        },
        {
          title: 'Teams evaluating optional AI replies',
          description:
            'AI replies require workspace configuration and available wallet credits. Human takeover and the customer-service window constrain automation; test your scenarios before enabling it.',
        },
        {
          title: 'Agencies and consultants with fast follow-up needs',
          description: 'Template-based WhatsApp messages maintain quality while increasing speed.',
        },
      ],
    },
    integrationTitle: 'Onboarding, costs and limits',
    integrationDescription:
      'The LeaderDaddy subscription and messaging costs are separate. Your Meta account controls phone eligibility, template approval and messaging limits. Configured services such as AI replies can use LeaderDaddy wallet credits.',
    integrationPoints: [
      'An administrator must complete the connection; Meta verification may require additional steps',
      'Obtain recipient opt-in and honor requests to stop messages',
      'Use approved templates to initiate conversations; free-form replies are limited to the customer-service window',
      'Connected-account broadcasts use Meta-direct billing; check current rates for the recipient market and message category',
      'Sent and replied recipient states do not prove a sale or guarantee delivery',
      'Business App coexistence is account-dependent; confirm eligibility during onboarding',
    ],
    benefitsTitle: 'Why WhatsApp matters in sales workflows',
    benefits: [
      {
        title: 'Conversation context',
        description: 'Keep customer messages available alongside the lead workflow.',
      },
      {
        title: 'Focused outreach',
        description: 'Build campaigns from accessible CRM leads or saved contact lists.',
      },
      {
        title: 'Next-step ownership',
        description: 'Use assigned leads and follow-up dates to coordinate the team after a reply.',
      },
    ],
    industriesTitle: 'Especially useful for',
    industries: [
      'Businesses that sell through WhatsApp conversations',
      'Teams with mobile-first prospects',
      'Small businesses with short response windows',
      'Agencies and consultants with fast follow-up needs',
      'Education and admissions teams',
      'Field sales teams',
    ],
    faqs: [
      {
        question: 'What does CRM with WhatsApp integration mean?',
        answer:
          'It means the CRM can send and receive WhatsApp messages tied to lead records, ownership, and workflow events instead of treating WhatsApp as a separate support channel.',
      },
      {
        question: 'Does LeaderDaddy replace WhatsApp?',
        answer:
          'No. LeaderDaddy complements WhatsApp by bringing messaging responsiveness into a structured CRM with lead tracking, follow-up discipline, and team accountability.',
      },
      {
        question: 'Is WhatsApp integration available in all plans?',
        answer:
          'WhatsApp-ready workflows require Meta WhatsApp Cloud API configuration. Contact the sales team to confirm availability for your workspace and plan.',
      },
      {
        question: 'Are messaging charges included in the annual plan?',
        answer:
          'No. The annual CRM subscription is separate from applicable Meta messaging charges. Connected-account broadcasts use Meta billing; other configured services, including AI replies, can consume wallet credits.',
      },
      {
        question: 'Is WhatsApp delivery guaranteed?',
        answer:
          'No. Consent, template approval, account quality, recipient eligibility and Meta limits affect sending and delivery. An approved template does not guarantee delivery or prevent account restrictions.',
      },
    ],
    resources: [
      {
        label: 'Plan a WhatsApp broadcast',
        href: '/whatsapp-broadcast-software',
      },
      {
        label: 'WhatsApp workflows for a small team',
        href: '/whatsapp-crm-for-small-business',
      },
      {
        label: 'LeaderDaddy subscription pricing',
        href: '/pricing',
      },
      {
        label: 'Meta messaging policy',
        href: 'https://business.whatsapp.com/policy',
      },
      {
        label: 'Meta Platform pricing',
        href: 'https://business.whatsapp.com/products/platform-pricing',
      },
    ],
  },
  '/whatsapp-crm-for-small-business': {
    path: '/whatsapp-crm-for-small-business',
    eyebrow: 'WhatsApp CRM for small business',
    heroTitle: 'WhatsApp CRM for a small team: assign leads and plan follow-ups',
    heroBody:
      'Give WhatsApp enquiries a lead owner, a status and a next action. Start with a small consenting test audience and check the complete follow-up workflow before rolling out to your team.',
    heroCta: 'Try WhatsApp CRM features',
    featureOverview: {
      title: 'WhatsApp-First Sales CRM',
      description:
        'Bridge messaging responsiveness with structured CRM tracking for Indian small businesses.',
      icon: 'smartphone',
    },
    technicalDetails: {
      title: 'How LeaderDaddy powers WhatsApp sales workflows',
      description:
        'Start by connecting an eligible WhatsApp Business account, assigning team roles and testing a customer conversation. Keep the enquiry in your lead workflow, choose an owner and set the next follow-up date.',
      bullets: [
        'Have an administrator connect the business account and sync templates',
        'Assign lead owners and agree on follow-up stages',
        'Check contact details and consent before outreach',
        'Test the inbox with a consenting contact',
        'Record a next action and follow-up date',
        'Review overdue follow-ups together before expanding rollout',
      ],
    },
    useCases: {
      title: 'Why Indian small businesses need WhatsApp CRM',
      description:
        'For teams whose customers already use WhatsApp, a shared CRM can make lead ownership and the next follow-up easier to track.',
      items: [
        {
          title: 'Retail and local service businesses',
          description: 'Convert WhatsApp inquiries into tracked leads with follow-up reminders.',
        },
        {
          title: 'Education institutes and coaching centers',
          description:
            'Manage admission inquiries and follow-ups through WhatsApp-linked lead records.',
        },
        {
          title: 'Real estate brokers and consultants',
          description:
            'Track property inquiries and follow-up schedules from WhatsApp conversations.',
        },
        {
          title: 'B2B distributors and traders',
          description: 'Handle order inquiries and follow-ups on WhatsApp with CRM accountability.',
        },
      ],
    },
    integrationTitle: 'WhatsApp CRM integrations',
    integrationDescription: 'LeaderDaddy connects WhatsApp messaging with core CRM workflows.',
    integrationPoints: [
      'Meta WhatsApp Cloud API where configured',
      'Approved account templates for appropriate outreach',
      'Recipient previews before campaign sending',
      'Lead context alongside customer conversations',
      'Follow-up reminders and overdue visibility',
      'INR subscription billing with Razorpay',
    ],
    benefitsTitle: 'What to evaluate for your Indian sales team',
    benefits: [
      {
        title: 'WhatsApp-ready notification workflows',
        description: 'Send quick WhatsApp updates and reminders directly from CRM lead triggers.',
      },
      {
        title: 'INR billing and Razorpay checkout',
        description:
          'Built for Indian businesses with native INR billing and easy digital payments.',
      },
      {
        title: 'Hindi and English interface support',
        description:
          'English and Hindi interface options; confirm coverage for your team during the trial.',
      },
    ],
    industriesTitle: 'Tailored for Indian SMBs and MSMEs',
    industries: [
      'Indian small businesses and traders',
      'Local service providers and agencies',
      'Education institutes and coaching centers',
      'Real estate brokers and property consultants',
      'Retail and B2B distributors in India',
    ],
    faqs: [
      {
        question: 'What is a WhatsApp CRM for small business?',
        answer:
          'It is a sales CRM that connects WhatsApp messaging workflows with lead pipeline management, team assignments, and follow-up tracking.',
      },
      {
        question: 'How does LeaderDaddy power WhatsApp sales workflows?',
        answer:
          'Start by connecting an eligible WhatsApp Business account, assigning team roles and testing a customer conversation. Keep the enquiry in your lead workflow, choose an owner and set the next follow-up date.',
      },
      {
        question: 'Is LeaderDaddy built for Indian businesses?',
        answer:
          'LeaderDaddy supports INR billing via Razorpay and English and Hindi interface options. Check account eligibility and budget separately for Meta messaging and configured wallet services.',
      },
    ],
    resources: [
      {
        label: 'WhatsApp account setup and capabilities',
        href: '/crm-with-whatsapp-integration',
      },
      {
        label: 'Campaign audiences, costs and consent',
        href: '/whatsapp-broadcast-software',
      },
      {
        label: 'Meta messaging policy',
        href: 'https://business.whatsapp.com/policy',
      },
      {
        label: 'Meta Platform pricing',
        href: 'https://business.whatsapp.com/products/platform-pricing',
      },
    ],
  },
  '/whatsapp-broadcast-software': {
    path: '/whatsapp-broadcast-software',
    eyebrow: 'WhatsApp broadcast software',
    heroTitle: 'WhatsApp broadcast software for CRM teams',
    heroBody:
      'Send or schedule WhatsApp template campaigns to consenting CRM audiences. Preview recipients, track replies and review Meta messaging costs before launch.',
    heroCta: 'Start your 7-day CRM trial',
    featureOverview: {
      title: 'CRM-Linked WhatsApp Broadcasts',
      description:
        'Build, preview, schedule, and monitor template-based WhatsApp campaigns without separating the audience from your CRM.',
      icon: 'messageSquare',
    },
    technicalDetails: {
      title: 'How a WhatsApp broadcast works in LeaderDaddy',
      description:
        'After a workspace connects a WhatsApp Business account, an authorized user selects a synced template and sender, chooses CRM leads or saved contact lists, validates template variables, previews recipients, and sends now or schedules the campaign.',
      bullets: [
        'Connect a workspace WhatsApp Business account before creating a campaign',
        'Choose a sender-specific, Meta-synced template and validate its required variables',
        'Build an audience from accessible CRM lead filters or saved contact lists',
        'Preview recipients and personalized template values before sending',
        'Send immediately or schedule a campaign for a future time',
        'Review sent, pending, failed, and replied recipient states after launch',
      ],
    },
    useCases: {
      title: 'When CRM-linked broadcasting is useful',
      description:
        'Use a targeted broadcast when the next sales action depends on a known audience, a verified template, and a follow-up record—not a disconnected contact export.',
      items: [
        {
          title: 'Event and appointment reminders',
          description:
            'Select the relevant lead audience, validate reminder variables, and schedule an approved template.',
        },
        {
          title: 'Product or service updates',
          description:
            'Reach a saved contact list or filtered CRM segment while preserving the campaign and recipient context.',
        },
        {
          title: 'Sales re-engagement',
          description:
            'Create a campaign for leads at a particular stage or follow-up state, then use replies in the CRM workflow.',
        },
        {
          title: 'Operational notifications',
          description:
            'Use the appropriate template for updates that need a clear sender, audience, and delivery-status trail.',
        },
      ],
    },
    integrationTitle: 'Compliance, costs, and campaign controls',
    integrationDescription:
      'WhatsApp broadcasts depend on the connected Meta WhatsApp Business account and its approved templates. Build audiences from contacts with the appropriate consent, and review current Meta rules, messaging limits, and charges in your Meta business setup before launching a campaign.',
    integrationPoints: [
      'Connected WhatsApp Business accounts are selected as the campaign sender',
      'Templates are scoped to the chosen sender account and must be available before launch',
      'Review who will receive the message; duplicate audience numbers are removed',
      'The annual LeaderDaddy CRM subscription is separate from Meta messaging charges',
      'Meta billing, message eligibility, delivery, and sending limits remain provider-controlled',
      'Replies are tracked against broadcast recipients so teams can continue the CRM follow-up',
    ],
    benefitsTitle: 'What the sales team keeps in one workflow',
    benefits: [
      {
        title: 'Audience context',
        description:
          'Use CRM lead filters or saved contact lists instead of recreating an audience in a separate tool.',
      },
      {
        title: 'Pre-send checks',
        description:
          'Validate required variables and preview the personalized message before a campaign is launched.',
      },
      {
        title: 'Follow-up visibility',
        description:
          'Review sent, pending, failed, and replied states to decide the next sales action.',
      },
    ],
    industriesTitle: 'Useful for teams that manage follow-ups in a CRM',
    industries: [
      'Education and admissions teams',
      'Real estate and property inquiry teams',
      'B2B distributors and account managers',
      'Retail and local service businesses',
      'Agencies coordinating client campaigns',
    ],
    faqs: [
      {
        question: 'What is WhatsApp broadcast software?',
        answer:
          'WhatsApp broadcast software helps a business send a template-based campaign to a selected audience and monitor the resulting recipient states. In LeaderDaddy, that audience can come from CRM lead filters or saved contact lists.',
      },
      {
        question: 'Is a WhatsApp broadcast the same as a group?',
        answer:
          'No. A broadcast sends a message to individual recipients; it does not create a shared group conversation. LeaderDaddy sends template campaigns through a connected WhatsApp Business account, then tracks recipient states and replies in the CRM workflow.',
      },
      {
        question: 'Can I schedule a WhatsApp broadcast in LeaderDaddy?',
        answer:
          'Yes. After choosing a connected sender account, a compatible template, and an audience, an authorized user can send a campaign immediately or set a future schedule.',
      },
      {
        question: 'Can a small business use WhatsApp broadcasts for customer updates?',
        answer:
          'A configured workspace can build a customer update campaign from CRM lead filters or saved contact lists, preview the recipients, and send now or schedule it with an available template. Contact people only with appropriate consent, and check Meta messaging charges separately from the CRM subscription.',
      },
      {
        question: 'How are WhatsApp broadcast costs handled?',
        answer:
          'The annual LeaderDaddy subscription is separate from Meta messaging charges. Connected-account broadcasts use Meta-direct billing. Rates depend on recipient market and message category; check current Meta pricing before sending. Other configured services, including AI replies, may use wallet credits.',
      },
      {
        question: 'Do I need consent before sending a WhatsApp broadcast?',
        answer:
          'Yes. Obtain opt-in permission before contacting recipients and honor requests to stop messages. A saved contact or CRM lead is not proof of consent. Review the audience before sending; template approval does not replace that responsibility.',
      },
    ],
    resources: [
      {
        label: 'WhatsApp CRM integration and onboarding',
        href: '/crm-with-whatsapp-integration',
      },
      {
        label: 'LeaderDaddy subscription pricing',
        href: '/pricing',
      },
      {
        label: 'Meta messaging policy',
        href: 'https://business.whatsapp.com/policy',
      },
      {
        label: 'Meta Platform pricing',
        href: 'https://business.whatsapp.com/products/platform-pricing',
      },
    ],
  },
  '/sales-dashboard-software': {
    path: '/sales-dashboard-software',
    eyebrow: 'Sales dashboard software',
    heroTitle: 'Real-time sales dashboard software for pipeline and team visibility',
    heroBody:
      'Gain instant visibility into active leads, overdue follow-ups, team performance, and conversion trends with clear, actionable sales dashboards.',
    heroCta: 'View sales dashboard features',
    featureOverview: {
      title: 'Sales Dashboard & Analytics',
      description:
        'Real-time pipeline visibility, overdue follow-up alerts, team performance metrics, and lead reports.',
      icon: 'barChart',
    },
    technicalDetails: {
      title: 'How LeaderDaddy delivers dashboard analytics',
      description:
        'LeaderDaddy includes real-time pipeline visualizers, team activity lists, conversion summaries, and downloadable reports in one centralized dashboard.',
      bullets: [
        'Pipeline stage metrics show total leads, stage distribution, and deal progress',
        'Overdue follow-up visibility highlights bottlenecked follow-ups for fast intervention',
        'Team performance signals compare rep activity, conversion rates, and closed opportunities',
        'Conversion summaries track lead-to-customer movement over time',
        'Downloadable reports for offline analysis and team meetings',
        'Saved filters let managers focus on specific products, assignees, or date ranges',
      ],
    },
    useCases: {
      title: 'Who needs sales dashboard software',
      description:
        'Metrics-driven leaders who need daily visibility without rebuilding spreadsheets.',
      items: [
        {
          title: 'Sales directors and revenue leaders',
          description: 'Monitor pipeline health and team execution from a single dashboard.',
        },
        {
          title: 'Small business owners and founders',
          description: 'See what is happening across the sales team without chasing updates.',
        },
        {
          title: 'Team leads managing sales reps',
          description:
            'Coach performance with real-time data on activity, conversions, and follow-ups.',
        },
        {
          title: 'Operations and pipeline managers',
          description: 'Identify bottlenecks and intervene before revenue drops.',
        },
      ],
    },
    integrationTitle: 'Dashboard data sources',
    integrationDescription: 'LeaderDaddy dashboards pull from live lead, activity, and team data.',
    integrationPoints: [
      'Real-time lead status and pipeline stage distribution',
      'Team activity lists with assignee and follow-up context',
      'Conversion rate calculations from lead-to-close movement',
      'Overdue follow-up alerts with automatic escalation',
      'Team ranking by activity volume and conversion metrics',
      'Exportable reports in CSV and PDF formats',
    ],
    benefitsTitle: 'Why dashboard visibility drives sales growth',
    benefits: [
      {
        title: 'Pipeline stage metrics',
        description: 'See total leads, stage distribution, and deal progress at a glance.',
      },
      {
        title: 'Overdue follow-up visibility',
        description: 'Spot bottlenecked follow-ups immediately so managers can intervene fast.',
      },
      {
        title: 'Team performance signals',
        description: 'Compare rep activity, conversion rates, and closed opportunities.',
      },
    ],
    industriesTitle: 'Designed for metrics-driven leaders',
    industries: [
      'Sales directors and revenue leaders',
      'Small business owners and founders',
      'Team leads managing sales reps',
      'Operations and pipeline managers',
      'Agencies tracking client lead output',
    ],
    faqs: [
      {
        question: 'What is sales dashboard software?',
        answer:
          'It aggregates sales metrics into visual charts and tables showing pipeline health, team activity, lead status, and conversion bottlenecks.',
      },
      {
        question: 'How does LeaderDaddy deliver dashboard analytics?',
        answer:
          'LeaderDaddy includes real-time pipeline visualizers, team activity lists, conversion summaries, and downloadable reports in one centralized dashboard.',
      },
      {
        question: 'Can I export dashboard reports?',
        answer:
          'Yes. LeaderDaddy supports downloadable reports for offline analysis and team meetings.',
      },
    ],
  },
};
